import * as am5 from '@amcharts/amcharts5';
import { ElementRef, InjectionToken } from '@angular/core';
import { z } from 'zod';

export const TL_MAP_LT_URL = new InjectionToken<string>('TL_MAP_LT_URL');
export const TL_REGIONAL_RATES_URL = new InjectionToken<string>(
    'TL_REGIONAL_RATES_URL',
);


export const TRENDLINES_VAN_MAP_RATE_DOC = 'trendlines_van_map_rate_doc';
export const TRENDLINES_REEFER_MAP_RATE_DOC = 'trendlines_reefer_map_rate_doc';
export const TRENDLINES_FLATBED_MAP_RATE_DOC =
    'trendlines_flatbed_map_rate_doc';

export const TRENDLINES_CACHE_DB = 'trendlines-cache';
export const TRENDLINES_DC_VAN_LT_MAP_DOC = 'trendlines_dc_van_lt_map';
export const TRENDLINES_DC_REEFER_LT_MAP_DOC = 'trendlines_dc_reefer_lt_map';
export const TRENDLINES_DC_FLATBED_LT_MAP_DOC = 'trendlines_dc_flatbed_lt_map';


export type LtColorRangesSchema = {
    min: number;
    max: number;
    inclusiveMax: number;
    color: am5.Color;
    isMax?: boolean;
};

export const updateLtMapPolygonStyle = (
    colorRanges: LtColorRangesSchema[],
    data: LtGeoData[],
): LtGeoData[] => {
    return data?.map((d) => {
        const colorRange: LtColorRangesSchema | undefined = colorRanges.find(
            (c) => {
                return d.value >= c.min && d.value <= c.inclusiveMax;
            },
        );
        const fill = colorRange?.color ?? d.fill;
        const valueFormatted =
            colorRange?.max === Number.MAX_SAFE_INTEGER
                ? `(${colorRange?.min}+)`
                : `(${colorRange?.min} - ${colorRange?.max})`;

        return {
            ...d,
            fill,
            valueFormatted,
            polygonSettings: { fill },
        };
    });
};

export type RatesResponse = {
    regionCode: string;
    tripRatePerMileInDollars: number;
    stateInRegion: string[];
};

export type LtGeoData = {
    id: string;
    geoCode: string;
    value: number;
    valueFormatted?: string;
    minFormatted?: string;
    maxFormatted?: string;
    fill: am5.Color;
    polygonSettings: {
        fill: am5.Color;
    };
};
export type RatesGeoData = {
    id: string;
    geoCode: string;
    value: number;
    valueFormatted?: string;
    minFormatted?: string;
    maxFormatted?: string;
    fill: am5.Color;
    polygonSettings: any;
    polygonSettings2: any;
};

export type Regions =
    | 'west'
    | 'southwest'
    | 'southeast'
    | 'northeast'
    | 'midwest';

export type RegionValues = Record<Regions, number>;
export type RegionGeoJson = Record<Regions, any>;
export type RegionData = Record<Regions, RatesGeoData[]>;

export type GeoJsonFeature = {
    type: string;
    properties: Partial<{
        code: string;
        name: string;
    }>;
    geometry: {
        type: 'Point';
        coordinates: number[];
    };
    id?: string;
};

export type GeoJson = {
    type: string;
    name: string;
    features: GeoJsonFeature[];
};

export const GeoLtSchema = z.object({
    geoCode: z.string(),
    lt: z.number(),
});

export type GeoLt = z.infer<typeof GeoLtSchema>;

export interface IIqTrendlinesMapStateService {
    mapLtFlatbed(container: ElementRef<any>, data: LtGeoData[]);
    mapLtVan(container: ElementRef<any>, data: LtGeoData[]);
    mapLtReefer(container: ElementRef<any>, data: LtGeoData[]);
    dataLtFlatbed(): Promise<LtGeoData[]>;
    dataLtVan(): Promise<LtGeoData[]>;
    dataLtReefer(): Promise<LtGeoData[]>;
    dataRateFlatbed(): Promise<RegionValues>;
    dataRateVan(): Promise<RegionValues>;
    dataRateReefer(): Promise<RegionValues>;
}

export const RateReeferColor = am5.color('#00D9FC');
export const RateFlatbedColor = am5.color('#BA61EB');
export const RateVanColor = am5.color('#FFBF00');

export const ratesData = (
    geoJson: GeoJson,
    color: am5.Color,
): RatesGeoData[] => {
    return geoJson.features.map(
        (feature: GeoJsonFeature, i: number): RatesGeoData => {
            return {
                id: feature.id,
                geoCode: feature.properties.code,
                valueFormatted: '',
                value: 1,
                minFormatted: '',
                maxFormatted: '',
                fill: am5.color('#fff'),
                polygonSettings: {
                    fill: am5.color('#ff0'),
                    opacity: 0.5,
                },
                polygonSettings2: {
                    fill: am5.color('#000'),
                    opacity: 1,
                    stroke: color,
                    strokeWidth: 4,
                },
            };
        },
    );
};

export const ltMapFlatbedColorRanges: LtColorRangesSchema[] = [
    {
        min: 0,
        max: 3.3,
        inclusiveMax: 3.3999999999999999,
        color: am5.color('rgb(217, 199, 240)'),
    },
    {
        min: 3.4,
        max: 8.2,
        inclusiveMax: 8.2999999999999993,
        color: am5.color('rgb(196, 163, 232)'),
    },
    {
        min: 8.3,
        max: 17.9,
        inclusiveMax: 17.999999999999999,
        color: am5.color('rgb(173, 122, 224)'),
    },
    {
        min: 18,
        inclusiveMax: Number.MAX_SAFE_INTEGER,
        max: Number.MAX_SAFE_INTEGER,
        color: am5.color('rgb(153, 0, 245)'),
        isMax: true,
    },
];

export const ltMapReeferColorRanges: LtColorRangesSchema[] = [
    {
        min: 0,
        max: 2.2,
        inclusiveMax: 2.2999999999999997,
        color: am5.color('rgb(176, 242, 255)'),
    },
    {
        min: 2.3,
        max: 5.5,
        inclusiveMax: 5.5999999999999996,
        color: am5.color('rgb(0, 224, 255)'),
    },
    {
        min: 5.6,
        max: 11.9,
        inclusiveMax: 11.999999999999999,
        color: am5.color('rgb(0, 194, 229)'),
    },
    {
        min: 12,
        max: Number.MAX_SAFE_INTEGER,
        inclusiveMax: Number.MAX_SAFE_INTEGER,
        color: am5.color('rgb(0, 150, 176)'),
        isMax: true,
    },
];

export const ltMapVanColorRanges: LtColorRangesSchema[] = [
    {
        min: 0,
        max: 1,
        inclusiveMax: 1.099999999999999989,
        color: am5.color('rgb(255, 235, 178)'),
    },
    {
        min: 1.1,
        max: 2.5,
        inclusiveMax: 2.5999999999999996,
        color: am5.color('rgb(255, 209, 77)'),
    },
    {
        min: 2.6,
        max: 5.4,
        inclusiveMax: 5.4999999999999995,
        color: am5.color('rgb(255, 173, 0)'),
    },
    {
        min: 5.5,
        max: Number.MAX_SAFE_INTEGER,
        inclusiveMax: Number.MAX_SAFE_INTEGER,
        color: am5.color('rgb(255, 107, 8)'),
        isMax: true,
    },
];
